import { useMemo, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  CollaboratorDataRow,
  CollaboratorDataRowAdapter,
} from "./CollaboratorDataRow";
import { ColDef, IDetailCellRendererParams } from "@ag-grid-community/core";
import { useRecordedService } from "../../api/recordedService";
import { formatCurrency, formatPercentage } from "../../utils/numberFormatters";

const CollaboratorAnalytics = () => {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const [rowData, setRowData] = useState<CollaboratorDataRow[]>();
  const { recorded } = useRecordedService();

  useEffect(() => {
    if (!recorded?.length) return;
    const finalOutput = new CollaboratorDataRowAdapter().ToCollabData(recorded);
    setRowData(finalOutput);
  }, [recorded]);

  const defaultColDef = useMemo<ColDef<CollaboratorDataRow>>(() => {
    return {
      flex: 1,
      aggFunc: "count",
      minWidth: 250,
      filter: true,
      resizable: true,
      enableRowGroup: true,
    };
  }, []);

  const [columnDefs] = useState<ColDef<CollaboratorDataRow>[]>([
    {
      field: "legalName",
      headerName: "Legal Name",
      cellRenderer: "agGroupCellRenderer",
    },
    { field: "artistName", headerName: "Stage Name" },
    { field: "email", headerName: "Email" },
    {
      field: "totalRevenue",
      headerName: "Total Revenue",
      valueFormatter: (p: any) =>
        formatCurrency(p.value || 0),
    },
    {
      field: "totalExpenses",
      headerName: "Total Expenses",
      valueFormatter: (p: any) =>
        formatCurrency(p.value || 0),
    },
    {
      field: "netProfit",
      headerName: "Net Profit",
      valueFormatter: (p: any) =>
        formatCurrency(p.value || 0),
    },
  ]);

  // And update the detail cell renderer params for the nested grid
  const detailCellRendererParams = useMemo<IDetailCellRendererParams>(() => {
    return {
      detailGridOptions: {
        columnDefs: [
          { field: "title", headerName: "Title" },
          {
            field: "split",
            headerName: "Split",
              valueFormatter: (p: any) => `${formatPercentage(p.value || 0)}`
          },
          {
            field: "mechanicals",
            headerName: "Mechanicals",
            valueFormatter: (p: any) => formatCurrency(p.value || 0)
          },
          { field: "type", headerName: "Type" },
          {
            field: "publishingRights",
            headerName: "Publishing Rights",
            valueFormatter: (p: any) => (p.value ? "Yes" : "No"),
          },
          { field: "releaseDate", headerName: "Release Date" },
        ],
        defaultColDef: {
          flex: 1,
        },
      },
      getDetailRowData: (params: any) => {
        params.successCallback(params.data.children);
      },
    };
  }, []);

  const cellSelection = useMemo(() => {
    return true;
  }, []);

  return (
    <div style={containerStyle}>
      <div style={gridStyle} className="ag-theme-quartz">
        <AgGridReact<CollaboratorDataRow>
          rowData={rowData}
          masterDetail={true}
          columnDefs={columnDefs}
          sideBar={{ toolPanels: ["columns", "filters"] }}
          rowGroupPanelShow={"always"}
          enableCharts={true}
          cellSelection={cellSelection}
          defaultColDef={defaultColDef}
          detailCellRendererParams={detailCellRendererParams}
        />
      </div>
    </div>
  );
};

export default CollaboratorAnalytics;
